import { TypeSelectOption } from '@thefarmersfront/kitchen';

import { create } from 'zustand';

import { DEFAULT_INPUT_STATUS } from '~/constants';

import { TypeInputState } from '~/types/common.types';
import {
  TypeStockReleaseAgree,
  TypeStockReleaseGoods,
  TypeStockReleasePallet,
  TypeStockReleaseRecentInfo,
  TypeStockReleaseRegisterDetail,
} from '~/types/stock.types';

export type TypeRequestGoodsFieldStatus = {
  availableRequestQuantity: TypeInputState;
  defectiveRequestQuantity: TypeInputState;
};

export type TypeRegisterInfoState = Record<
  'detailInfoState' | 'palletInfoState' | 'agreeInfoState',
  Record<string, TypeInputState>
>;

type TypeStockReleaseStoreState = {
  cluster: TypeSelectOption<string> | null;
  requestGoods: TypeStockReleaseGoods[];
  requestGoodsState: TypeRequestGoodsFieldStatus[];
  detailInfo: Partial<TypeStockReleaseRegisterDetail> | null;
  detailInfoState: Record<keyof TypeStockReleaseRegisterDetail, TypeInputState>;
  palletInfo: Partial<TypeStockReleasePallet> | null;
  palletInfoState: Record<keyof TypeStockReleasePallet, TypeInputState>;
  agreeInfo: Partial<TypeStockReleaseAgree> | null;
  agreeInfoState: Record<keyof TypeStockReleaseAgree, TypeInputState>;
  recentInfo: TypeStockReleaseRecentInfo | null;
};

type TypeStockReleaseStoreActions = {
  /** 반출 요청 상품 관련 액션 */
  setCluster: (cluster: TypeSelectOption<string>) => void;
  setRequestGoods: (items: TypeStockReleaseGoods[]) => void;
  updateRequestGoods: (index: number, updates: Partial<TypeStockReleaseGoods>) => void;
  deleteRequestGoods: (indices: number[]) => void;
  resetRequestGoods: () => void;
  setRequestGoodsStatus: (newStatus: TypeRequestGoodsFieldStatus[]) => void;
  updateRequestGoodsStatus: (index: number, updates: Partial<TypeRequestGoodsFieldStatus>) => void;
  applyFullAvailableQuantity: () => void;
  applyFullDefectiveQuantity: () => void;

  /** 반출 요청 등록 데이터 관련 액션 */
  setDetailInfo: (detailInfo: Partial<TypeStockReleaseRegisterDetail>) => void;
  setPalletInfo: (palletInfo: Partial<TypeStockReleasePallet>) => void;
  resetPalletInfo: () => void;
  setAgreeInfo: (agreeInfo: Partial<TypeStockReleaseAgree>) => void;
  setRegisterInfoState: (infoState: Partial<TypeRegisterInfoState>) => void;
  setRecentInfo: (recentInfo: TypeStockReleaseRecentInfo | null) => void;

  /** 반출 요청 초기화 액션 */
  resetStockRelease: () => void;
  resetStockReleaseInfo: () => void;
};

type TypeStockReleaseStore = TypeStockReleaseStoreState & { actions: TypeStockReleaseStoreActions };

const initialRegisterFirstStepValue = {
  cluster: null,
  requestGoods: [],
  requestGoodsState: [],
};

const initialRegisterSecondStepValue = {
  detailInfo: null,
  detailInfoState: {
    method: DEFAULT_INPUT_STATUS,
    expectedDate: DEFAULT_INPUT_STATUS,
    packingMethod: DEFAULT_INPUT_STATUS,
    phoneNumber: DEFAULT_INPUT_STATUS,
    zipCode: DEFAULT_INPUT_STATUS,
    address: DEFAULT_INPUT_STATUS,
    detailAddress: DEFAULT_INPUT_STATUS,
  },
  palletInfo: null,
  palletInfoState: {
    palletType: DEFAULT_INPUT_STATUS,
    logisticsCode: DEFAULT_INPUT_STATUS,
    managerName: DEFAULT_INPUT_STATUS,
    managerPhoneNumber: DEFAULT_INPUT_STATUS,
    managerEmail: DEFAULT_INPUT_STATUS,
    zipCode: DEFAULT_INPUT_STATUS,
    address: DEFAULT_INPUT_STATUS,
    detailAddress: DEFAULT_INPUT_STATUS,
  },
  agreeInfo: null,
  agreeInfoState: {
    isCheckedPacking: DEFAULT_INPUT_STATUS,
    isCheckedDate: DEFAULT_INPUT_STATUS,
  },
};

const createDefaultFieldStatus = (): TypeRequestGoodsFieldStatus => ({
  availableRequestQuantity: DEFAULT_INPUT_STATUS,
  defectiveRequestQuantity: DEFAULT_INPUT_STATUS,
});

export const useStockReleaseStore = create<TypeStockReleaseStore>()((set, get) => ({
  ...initialRegisterFirstStepValue,
  ...initialRegisterSecondStepValue,
  recentInfo: null,
  actions: {
    /** 반출 출발지 센터 저장 */
    setCluster: (cluster) => set({ cluster }),
    /** 반출 상품 추가 */
    setRequestGoods: (items) => {
      const currentRequestGoods = get().requestGoods;
      const currentStatus = get().requestGoodsState;

      // 반출 요청 재고 상품 중복 방지
      const existingSkuCodes = new Set(currentRequestGoods.map((item) => item.skuCode));
      const newItems = items.filter((item) => !existingSkuCodes.has(item.skuCode));

      // 새로운 아이템들에 대한 기본 상태 생성
      const newStatus = newItems.map(() => createDefaultFieldStatus());

      set({
        requestGoods: [...newItems, ...currentRequestGoods],
        requestGoodsState: [...newStatus, ...currentStatus],
      });
    },
    /** 반출 상품 정보 변경 */
    updateRequestGoods: (index, updates) => {
      const currentRequestGoods = get().requestGoods;

      if (index < 0 || index >= currentRequestGoods.length) {
        return;
      }

      const newRequestGoods = [...currentRequestGoods];
      if (newRequestGoods[index]) {
        newRequestGoods[index] = {
          ...newRequestGoods[index],
          ...updates,
        };
        set({ requestGoods: newRequestGoods });
      }
    },
    /** 반출 상품 삭제 */
    deleteRequestGoods: (indices) => {
      const newRequestGoods = get().requestGoods.filter((_, index) => !indices.includes(index));
      const newStatus = get().requestGoodsState.filter((_, index) => !indices.includes(index));

      set({ requestGoods: newRequestGoods, requestGoodsState: newStatus });
    },
    /** 반출 상품 초기화 */
    resetRequestGoods: () => set(initialRegisterFirstStepValue),
    /** 반출 상품 상태 저장 */
    setRequestGoodsStatus: (newStatus) => set({ requestGoodsState: newStatus }),
    /** 반출 상품 상태 변경 */
    updateRequestGoodsStatus: (index, updates) => {
      const currentStatus = get().requestGoodsState;
      const newStatus = [...currentStatus];

      if (!newStatus[index]) {
        newStatus[index] = createDefaultFieldStatus();
      }

      newStatus[index] = {
        ...newStatus[index],
        ...updates,
      };

      set({ requestGoodsState: newStatus });
    },
    /** 가용 재고 전량 반출 적용 */
    applyFullAvailableQuantity: () => {
      const currentRequestGoods = get().requestGoods;
      const currentStatus = get().requestGoodsState;

      set({
        requestGoods: currentRequestGoods.map((item) => ({
          ...item,
          availableRequestQuantity: item.availableQuantity,
        })),
        requestGoodsState: currentStatus.map(() => createDefaultFieldStatus()),
      });
    },
    /** 불용 재고 전량 반출 적용 */
    applyFullDefectiveQuantity: () => {
      const currentRequestGoods = get().requestGoods;
      const currentStatus = get().requestGoodsState;

      set({
        requestGoods: currentRequestGoods.map((item) => ({
          ...item,
          defectiveRequestQuantity: item.defectiveQuantity,
        })),
        requestGoodsState: currentStatus.map(() => createDefaultFieldStatus()),
      });
    },
    /** 반출 예정 정보 상태 업데이트 */
    setDetailInfo: (detailInfo) => set({ detailInfo: { ...get().detailInfo, ...detailInfo } }),
    /** 파렛트 전표 정보 상태 업데이트 */
    setPalletInfo: (palletInfo) => set({ palletInfo: { ...get().palletInfo, ...palletInfo } }),
    resetPalletInfo: () => {
      const recentInfo = get().recentInfo;
      set({ palletInfo: recentInfo?.palletInfo, palletInfoState: initialRegisterSecondStepValue.palletInfoState });
    },
    /** 동의 사항 상태 업데이트 */
    setAgreeInfo: (agreeInfo) => set({ agreeInfo: { ...get().agreeInfo, ...agreeInfo } }),
    /** 반출 요청 등록 정보 상태 저장 */
    setRegisterInfoState: (infoState) => {
      const currentState = get();
      set({
        detailInfoState: { ...currentState.detailInfoState, ...infoState?.detailInfoState },
        palletInfoState: { ...currentState.palletInfoState, ...infoState?.palletInfoState },
        agreeInfoState: { ...currentState.agreeInfoState, ...infoState?.agreeInfoState },
      });
    },
    /** 최근 반출 도착지 정보 상태 업데이트 */
    setRecentInfo: (recentInfo) => {
      set({ recentInfo, detailInfo: recentInfo?.destinationAddress, palletInfo: recentInfo?.palletInfo });
    },
    /** 반출 요청 초기화 */
    resetStockRelease: () => {
      set({ ...initialRegisterFirstStepValue, ...initialRegisterSecondStepValue, recentInfo: null });
    },
    resetStockReleaseInfo: () => {
      const recentInfo = get().recentInfo;
      set({
        ...initialRegisterSecondStepValue,
        detailInfo: recentInfo?.destinationAddress,
        palletInfo: recentInfo?.palletInfo,
      });
    },
  },
}));
